<template>
  <div class="page">
    <div class="page-header">
      <div>
        <h1>Copie prod → pré-prod</h1>
        <p>Remet la pré-prod à l'identique de la prod, avec les données de cette nuit.</p>
      </div>
    </div>

    <div
      v-if="$env.key !== 'preprod'"
      class="panel notice">
      <v-icon
        color="warning"
        class="mr-3">
        mdi-information-outline
      </v-icon>
      Passe sur l'environnement Pré-prod (en haut du menu) pour lancer une copie.
    </div>

    <template v-else>
      <div class="refresh-grid">
        <div class="panel">
          <div class="panel__title">
            Ce qui se passe quand tu cliques
          </div>
          <div class="panel__body">
            <ul class="refresh-rules">
              <li>Les données de la pré-prod sont remplacées par celles de la prod.</li>
              <li>Les comptes de test et les clés API de la pré-prod sont gardés.</li>
              <li>Aucun mail, SMS ou notification ne part vers les clients.</li>
              <li>En cas de problème, on peut revenir à la pré-prod d'avant.</li>
            </ul>
            <div class="banner-row">
              <span class="cell-muted">Environ 2 minutes. La pré-prod est coupée quelques secondes à la fin.</span>
              <v-spacer />
              <v-btn
                color="error"
                depressed
                :loading="starting"
                :disabled="loading || isRunning || unavailable"
                @click="start">
                Lancer la copie
              </v-btn>
            </div>
          </div>
        </div>

        <div class="panel">
          <div class="panel__title">
            Dernière copie
          </div>
          <div class="panel__body">
            <div
              v-if="unavailable"
              class="cell-muted">
              Pas encore disponible : l'API de la pré-prod doit être redéployée.
            </div>
            <div
              v-else-if="state && state.status"
              class="refresh-state">
              <span :class="statusPill.class">{{ statusPill.label }}</span>
              <span
                v-if="state.requested_at"
                class="cell-muted">
                Demandée le {{ state.requested_at | dtFormat('DD/MM/YYYY à HH:mm') }} par {{ state.requested_by }}
              </span>
              <span
                v-if="state.finished_at"
                class="cell-muted">
                Terminée le {{ state.finished_at | dtFormat('DD/MM/YYYY à HH:mm') }}
              </span>
              <span
                v-if="state.error"
                class="refresh-error">{{ state.error }}</span>
            </div>
            <div
              v-else-if="loading"
              class="cell-muted">
              Chargement…
            </div>
            <div
              v-else
              class="cell-muted">
              Aucune copie pour l'instant.
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="state && state.log && state.log.length && ['failed', 'reindex_failed'].includes(state.status)"
        class="panel mt-4">
        <div class="panel__title">
          Détail de l'erreur
        </div>
        <div class="panel__body">
          <pre class="refresh-log">{{ state.log.join('\n') }}</pre>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
  const STATUS = {
    requested: { label: 'Démarrage…', class: 'pill pill--info' },
    running: { label: 'Copie en cours', class: 'pill pill--warning' },
    done: { label: 'Copiée, mise à jour de la recherche…', class: 'pill pill--warning' },
    reindexing: { label: 'Copiée, mise à jour de la recherche…', class: 'pill pill--warning' },
    finished: { label: 'Terminée', class: 'pill pill--success' },
    failed: { label: 'Échec : la pré-prod n\'a pas été modifiée', class: 'pill pill--danger' },
    reindex_failed: { label: 'Copiée, mais la recherche n\'a pas pu être mise à jour', class: 'pill pill--danger' }
  }

  // Copie de la base prod vers la pré-prod : la route dépose une demande, l'outil Coolify la traite
  export default {
    name: 'PreprodRefresh',
    data () {
      return {
        state: null,
        loading: false,
        starting: false,
        unavailable: false,
        timer: null
      }
    },
    computed: {
      isRunning () {
        return !!this.state && ['requested', 'running', 'done', 'reindexing'].includes(this.state.status)
      },
      statusPill () {
        return STATUS[this.state.status] || { label: this.state.status, class: 'pill' }
      }
    },
    created () {
      if (this.$env.key === 'preprod') this.fetch()
    },
    beforeDestroy () {
      clearTimeout(this.timer)
    },
    methods: {
      async fetch () {
        this.loading = true
        try {
          const { data } = await this.$api.axios.get('admin/preprod-refresh')
          this.state = data
          this.unavailable = false
        } catch (e) {
          if (e.response && [404, 405].includes(e.response.status)) {
            this.unavailable = true
          } else if (e.response && e.response.status !== 502 && e.response.status !== 503) {
            this.$awn.alert('Impossible de lire l\'état de la copie')
          }
        } finally {
          this.loading = false
        }
        // suivi en direct pendant la copie (la pré-prod peut ne pas répondre quelques secondes à la bascule)
        clearTimeout(this.timer)
        if (this.isRunning) this.timer = setTimeout(this.fetch, 5000)
      },
      async start () {
        const ok = await this.$confirm({
          title: 'Copier la prod vers la pré-prod ?',
          message: 'Les données actuelles de la pré-prod seront remplacées par celles de la prod (l\'ancienne base est gardée jusqu\'à la copie suivante).',
          confirmLabel: 'Lancer la copie',
          danger: true
        })
        if (!ok) return
        this.starting = true
        try {
          const { data } = await this.$api.axios.post('admin/preprod-refresh')
          this.state = data
          this.$awn.success('Copie demandée')
          this.fetch()
        } catch (e) {
          if (e.response && e.response.status === 409) {
            this.$awn.warning('Une copie est déjà en cours')
          } else {
            this.$awn.alert('Impossible de lancer la copie')
          }
        } finally {
          this.starting = false
        }
      }
    }
  }
</script>

<style scoped>
  .refresh-grid {
    display: grid;
    grid-template-columns: minmax(0, 2fr) minmax(0, 1fr);
    gap: 16px;
  }
  .refresh-rules {
    margin: 0 0 16px;
    padding-left: 18px;
    color: #52525b;
    font-size: 13.5px;
    line-height: 1.7;
  }
  .banner-row {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .refresh-state {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }
  .refresh-error {
    color: #b91c1c;
    font-size: 12.5px;
  }
  .notice {
    display: flex;
    align-items: center;
    padding: 20px;
    color: #52525b;
  }
  .refresh-log {
    margin: 0;
    max-height: 320px;
    overflow: auto;
    font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
    font-size: 12px;
    line-height: 1.6;
    white-space: pre-wrap;
    color: #3f3f46;
  }
  @media (max-width: 900px) {
    .refresh-grid {
      grid-template-columns: 1fr;
    }
  }
</style>
